import { Check, Clock, X } from 'lucide-react';
import React from 'react';

import { Pill } from '@/components/common/Pill';
import { cn } from '@/lib/utils';

import { getPrerequisiteVariant, PREREQUISITE_LABELS } from './stage-config';
import type { PrerequisiteStatus, PrerequisiteSummary } from './types';
import { humanizePrereqStatus, isSatisfied } from './utils';

interface MiniTortPrerequisitesProps {
  prerequisites: PrerequisiteSummary;
  className?: string;
}

export const MiniTortPrerequisites: React.FC<MiniTortPrerequisitesProps> = ({
  prerequisites,
  className,
}) => {
  const entries = [
    { key: 'processing_status', status: prerequisites.processing_status },
    { key: 'review_status', status: prerequisites.review_status },
    { key: 'coverage_1p_status', status: prerequisites.coverage_1p_status },
    { key: 'coverage_3p_status', status: prerequisites.coverage_3p_status },
    { key: 'police_report_status', status: prerequisites.police_report_status },
  ];

  const satisfiedCount = entries.filter((e) => isSatisfied(e.status)).length;
  const progressPct = (satisfiedCount / entries.length) * 100;

  return (
    <div className={cn('rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900', className)}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Prerequisites</h3>
        <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
          {satisfiedCount}/{entries.length} complete
        </span>
      </div>

      <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
        <div
          className="h-full rounded-full bg-emerald-500 transition-all duration-500"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <div className="space-y-2">
        {entries.map((entry) => (
          <PrerequisiteRow
            key={entry.key}
            label={PREREQUISITE_LABELS[entry.key] ?? entry.key}
            status={entry.status as PrerequisiteStatus}
          />
        ))}
      </div>

      <div className="mt-3 flex items-center gap-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
        <ReadyIndicator label="All Ready" ready={prerequisites.all_ready} />
        <ReadyIndicator label="Demand Ready" ready={prerequisites.ready_for_demand} />
      </div>
    </div>
  );
};

const PrerequisiteRow: React.FC<{ label: string; status: PrerequisiteStatus }> = ({
  label,
  status,
}) => {
  const variant = getPrerequisiteVariant(status);
  const satisfied = isSatisfied(status);
  const failed = status === 'failed';

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {satisfied && <Check className="h-4 w-4 text-emerald-500" />}
        {!satisfied && failed && <X className="h-4 w-4 text-red-500" />}
        {!satisfied && !failed && <Clock className="h-4 w-4 text-zinc-400" />}
        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{label}</span>
      </div>
      <Pill variant={variant} size="sm">
        {humanizePrereqStatus(status)}
      </Pill>
    </div>
  );
};

const ReadyIndicator: React.FC<{ label: string; ready: boolean }> = ({ label, ready }) => (
  <div className="flex items-center gap-1.5">
    <div
      className={cn(
        'h-2 w-2 rounded-full',
        ready ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-600',
      )}
    />
    <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{label}</span>
  </div>
);
